import { AlertCircle, ChevronDown, ChevronRight } from "lucide-react";
import { observer } from "mobx-react-lite";
import { useCallback, useMemo, useState } from "react";
import {
  useDisclosureStore,
  useEntryDataStore,
  useEntryUIStore,
} from "../../hooks/useDataEntryStore";
import {
  filterOrgEntries,
  formatCarbonEmissionReduction,
  formatMetricValue,
  formatTotal,
} from "../../domains/dataEntry";
import {
  extractReferences,
  stripAnchor,
} from "@/features/esg/utils/formula-evaluator";
import { useAuthStore } from "@/features/auth";
import { useTran } from "@/hooks";
import { useCan } from "@/hooks/useCan/useCan";
import { isNxmapMappableDisclosure } from "@/features/esg/constants/nxmap-mappable-disclosures.constants";
import CarbonEmissionReductionBadge from "./CarbonEmissionReductionBadge";
import EntryFilterBar from "./EntryFilterBar";
import OrgGroup from "./OrgGroup";
import ReportingPeriodLabel from "../common/ReportingPeriodLabel";

const DependencyRow = ({ code, title, value, unit, status, onNavigate }) => {
  const { t } = useTran();
  const hasValue =
    (status === "available" || status === "computed") && value != null;
  const displayValue = hasValue
    ? unit
      ? `${formatMetricValue(value)} ${unit}`
      : formatMetricValue(value)
    : t("N/A");

  return (
    <div className="flex items-center justify-between py-1.5 text-sm">
      <span>
        <button
          type="button"
          className="font-medium text-primary underline"
          onClick={() => onNavigate(code)}
        >
          {code}
        </button>
        {title && <span className="text-muted-foreground ml-1">({title})</span>}
      </span>
      <span
        className={!hasValue ? "text-muted-foreground italic" : "font-medium"}
      >
        {displayValue}
      </span>
    </div>
  );
};

const YearGroup = ({
  year,
  formulaResult,
  dependencies,
  onNavigate,
  reportingYears,
  carbonEmissionMultiplier,
}) => {
  const { t } = useTran();
  const [expanded, setExpanded] = useState(true);
  const Chevron = expanded ? ChevronDown : ChevronRight;

  const total =
    formulaResult.status === "computed"
      ? formatTotal(formulaResult.value, formulaResult.unit)
      : t("N/A");
  const carbonEmissionReduction =
    formulaResult.status === "computed"
      ? formatCarbonEmissionReduction(
          formulaResult.value,
          carbonEmissionMultiplier,
        )
      : null;

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2.5">
        <button
          type="button"
          className="flex items-center gap-1.5 font-semibold text-sm"
          onClick={() => setExpanded((v) => !v)}
        >
          <Chevron size={14} />
          <ReportingPeriodLabel
            reportingYears={reportingYears}
            year={year}
            direction="horizontal"
          />
        </button>
      </div>

      {expanded && (
        <div className="px-4 pb-3 space-y-2">
          {/* Total banner */}
          <div className="flex items-center justify-between px-3 py-2 rounded-md border border-primary/20 bg-primary/5">
            <span className="text-sm font-semibold">{t("Total")}</span>
            <div className="flex items-center gap-3">
              <CarbonEmissionReductionBadge value={carbonEmissionReduction} />
              <span className="text-sm font-semibold text-primary">
                {total}
              </span>
            </div>
          </div>

          {/* Unable to compute warning */}
          {formulaResult.status === "incomplete" && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-amber-50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-400">
              <AlertCircle size={14} className="shrink-0" />
              <span className="text-xs">
                {t("Unable to compute — some dependencies are missing")}
              </span>
            </div>
          )}

          {/* Dependency rows — anchored refs (e.g. CODE@baseline) are hidden
              because they duplicate the same disclosure already listed by
              its bare code; the baseline year context lives in the formula
              header, not in the dep list. */}
          <div className="divide-y divide-border">
            {dependencies
              .filter((dep) => stripAnchor(dep.code) === dep.code)
              .map((dep) => (
                <DependencyRow
                  key={dep.code}
                  {...dep}
                  onNavigate={onNavigate}
                />
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

const mapYearData = (year, yearData) => {
  const { formulaResult, dependencies } = yearData;
  if (!formulaResult) return null;

  return {
    year: Number(year),
    formulaResult,
    dependencies: dependencies || [],
  };
};

const ComputedEntryView = observer(({ reportingYears = [] }) => {
  const { activeDisclosure, disclosures, setActiveDisclosure } =
    useDisclosureStore();
  const { computedEntries, orgList, orgLookupMap } = useEntryDataStore();
  const {
    selectedOrg,
    selectedYear,
    searchEntry,
    setShowCreateEntriesDialog,
    setCreateDialogPreset,
  } = useEntryUIStore();
  const { user } = useAuthStore();
  const { allowed: canCreateEntry } = useCan({
    userPermissions: user?.permissions || [],
    resource: "esg_data_entry",
    action: "create",
  });

  const hasNxmap = Boolean(user?.nxmapConnected);

  const handleNavigate = useCallback(
    (code, orgId, year) => {
      const disclosure = disclosures.find((d) => d.code === code);
      if (!disclosure) return;

      // Computed (formula-driven, non-subEntry) disclosures have no
      // manual inputs — drilling in should just switch the right pane
      // to that disclosure's computed view, not open a Create dialog.
      const isComputed =
        disclosure.isCalculated && disclosure.inputType !== "subEntries";
      // NxMap-mappable disclosures are auto-synced; DisclosureHeader hides
      // the "Add Entry" button for them. Mirror that here so the dep-link
      // drill-down doesn't pop the manual-entry dialog (which would create
      // rows the next sync clobbers).
      const isNxmapImport =
        hasNxmap &&
        !isComputed &&
        isNxmapMappableDisclosure(disclosure.framework, disclosure.code);
      const canOpenDialog = !isComputed && !isNxmapImport && canCreateEntry;

      if (canOpenDialog && orgId && year) {
        setCreateDialogPreset({ orgId, year });
      }
      setActiveDisclosure(disclosure);
      if (canOpenDialog) {
        setShowCreateEntriesDialog(true);
      }
    },
    [
      disclosures,
      canCreateEntry,
      hasNxmap,
      setActiveDisclosure,
      setCreateDialogPreset,
      setShowCreateEntriesDialog,
    ],
  );

  const orgMap = useMemo(
    () => new Map(orgList.map((org) => [org.id, org])),
    [orgList],
  );

  const filteredOrgs = useMemo(
    () =>
      filterOrgEntries(
        computedEntries?.dataEntries,
        orgMap,
        { selectedOrg, selectedYear, searchEntry },
        mapYearData,
        orgLookupMap,
      ),
    [
      computedEntries,
      orgMap,
      selectedOrg,
      selectedYear,
      searchEntry,
      orgLookupMap,
      orgLookupMap.size,
    ],
  );

  const hasEntries = Object.keys(computedEntries?.dataEntries || {}).length > 0;

  const placeholderYearGroup = useMemo(() => {
    if (hasEntries) return null;
    const { formula, defaultUnit } = activeDisclosure || {};
    if (!formula) return null;

    const codes = extractReferences(formula);
    const disclosureMap = new Map(disclosures.map((d) => [d.code, d]));

    return {
      year: null,
      formulaResult: {
        value: null,
        unit: defaultUnit || null,
        status: "incomplete",
      },
      dependencies: codes.map((code) => {
        const dep = disclosureMap.get(stripAnchor(code));
        return {
          code,
          title: dep?.title || null,
          disclosureId: dep?._id || null,
          value: null,
          unit: null,
          status: dep ? "missing" : "not_found",
        };
      }),
    };
  }, [hasEntries, activeDisclosure, disclosures]);

  return (
    <div className="space-y-4">
      <EntryFilterBar />

      {hasEntries ? (
        <div className="divide-y-[1px] divide-border">
          {filteredOrgs.map((org) => (
            <OrgGroup key={org.orgId} {...org}>
              {org.years.map((yg) => (
                <YearGroup
                  key={yg.year}
                  {...yg}
                  carbonEmissionMultiplier={
                    activeDisclosure?.carbonEmissionMultiplier
                  }
                  onNavigate={(code) =>
                    handleNavigate(code, org.orgId, yg.year)
                  }
                  reportingYears={reportingYears}
                />
              ))}
            </OrgGroup>
          ))}
        </div>
      ) : placeholderYearGroup ? (
        <div className="space-y-4">
          <YearGroup
            {...placeholderYearGroup}
            carbonEmissionMultiplier={
              activeDisclosure?.carbonEmissionMultiplier
            }
            onNavigate={handleNavigate}
          />
        </div>
      ) : null}
    </div>
  );
});

export default ComputedEntryView;
